import React from "react";
import { motion } from "framer-motion";
import { Calendar, Mail, Lock, Database, Shield, Eye, MessageSquare, Clock, UserCheck } from "lucide-react";

const Privacy = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial="initial"
          animate="animate"
          variants={stagger}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <div className="flex justify-center items-center gap-4 text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>Last updated: June 4, 2025</span>
            </div>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto">
              Voxel ("the Bot") is a Discord bot developed to provide entertainment and utility features within Discord servers. 
              This Privacy Policy explains what data we collect, how we use it, and your rights regarding that data.
            </p>
          </motion.div>

          {/* Content */}
          <motion.div variants={stagger} className="prose prose-invert max-w-none">
            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Eye className="w-6 h-6 text-green-400" />
                1. Data We Collect
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Voxel may collect and store the following data:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-800">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Database className="w-4 h-4 text-blue-400" />
                    Identifiers
                  </h4>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>User ID, Server ID, Channel ID</li>
                    <li>Used for feature tracking and functionality</li>
                  </ul>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-800">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-400" />
                    Usage Data
                  </h4>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Command usage statistics</li>
                    <li>Economy/game progress and preferences</li>
                  </ul>
                </div>
              </div>
              <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-yellow-400" />
                  Message Content (Limited Use)
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Message content is only accessed in specific features (such as counting games or message deletion logging), 
                  and is <strong>never stored persistently</strong>. Content is processed in real-time and discarded immediately after use.
                </p>
              </div>
              <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-500/20 mt-4">
                <p className="text-blue-200 text-sm">
                  <strong>Important:</strong> We do not collect or store personal information such as email addresses, 
                  passwords, or any third-party service credentials.
                </p>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Database className="w-6 h-6 text-blue-400" />
                2. How We Use Your Data
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed mb-4">We use your data to:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Provide bot features (games, economy, utility tools)</li>
                  <li>Maintain state across commands and sessions</li>
                  <li>Improve features through usage insights</li>
                  <li>Enforce abuse prevention measures</li>
                  <li>Enable cross-server economy functionality</li>
                  <li>Provide personalized gaming experiences</li>
                </ul>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-yellow-400" />
                3. Message Content Usage
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed mb-4">
                  Voxel may read message content in limited and clearly defined scenarios:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                  <li>To validate game actions (e.g., counting games)</li>
                  <li>To log deleted messages when configured by a server admin</li>
                  <li>To process giveaway entries and participation</li>
                </ul>
                <div className="bg-red-900/20 rounded-lg p-4 border border-red-500/20">
                  <p className="text-red-200 text-sm leading-relaxed">
                    <strong>Privacy Guarantee:</strong> Message content is never stored long-term, indexed, or analyzed 
                    outside of these specific, opt-in features. If a message must be logged (e.g., for moderation), 
                    it is only retained temporarily and only in servers where the logging feature is enabled by server administrators.
                  </p>
                </div>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-purple-400" />
                4. Data Sharing
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed mb-4">
                  <strong>We do not sell or share any user data with third parties.</strong> Data is only accessible to 
                  Voxel developers for debugging, support, or feature development when necessary.
                </p>
                <div className="bg-green-900/20 rounded-lg p-4 border border-green-500/20">
                  <h4 className="font-semibold text-green-200 mb-2">Third-Party Services</h4>
                  <p className="text-green-200 text-sm">
                    We integrate with top.gg for server statistics display on our website. 
                    Only publicly available bot statistics are shared with this service.
                  </p>
                </div>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Clock className="w-6 h-6 text-orange-400" />
                5. Data Retention
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-800">
                  <h4 className="font-semibold text-white mb-2">Permanent Storage</h4>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Economy and gameplay data until removal requested</li>
                    <li>User preferences and settings</li>
                  </ul>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-800">
                  <h4 className="font-semibold text-white mb-2">Temporary Storage</h4>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Logs: Up to 60 days</li>
                    <li>Message content: Not stored</li>
                    <li>Session data: Until session ends</li>
                  </ul>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-4">
                Message content is not stored, except briefly and only when required for a feature the server has explicitly enabled.
              </p>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <UserCheck className="w-6 h-6 text-teal-400" />
                6. Your Rights
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Data Access</h4>
                    <p className="text-gray-300 text-sm">Request a copy of all stored data</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Data Deletion</h4>
                    <p className="text-gray-300 text-sm">Full removal of all your data</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Opt-Out</h4>
                    <p className="text-gray-300 text-sm">Stop all future data collection</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Data Portability</h4>
                    <p className="text-gray-300 text-sm">Export your data in readable format</p>
                  </div>
                </div>
                <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-500/20">
                  <p className="text-blue-200 text-sm">
                    <strong>Easy Process:</strong> Contact us with your Discord user ID to exercise any of these rights. 
                    We will comply with all requests within a reasonable time frame.
                  </p>
                </div>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Children's Privacy</h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed mb-4">
                  Our service complies with Discord's minimum age requirements (13+ years in most regions). 
                  We do not knowingly collect personal information from children under Discord's minimum age requirements.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  If you are a parent or guardian and believe your child has provided us with personal information, 
                  please contact us immediately for data removal.
                </p>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. Changes to This Policy</h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time. We will notify users of any significant changes 
                  through our Discord support server or by updating the "Last updated" date at the top of this policy.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Continued use of Voxel after changes constitutes acceptance of the updated Privacy Policy.
                </p>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">9. Contact Us</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                For questions, support, or data removal requests, contact us via:
              </p>
              <div className="bg-slate-900 rounded-lg p-6 flex items-center gap-4">
                <Mail className="w-6 h-6 text-green-400" />
                <div>
                  <a 
                    href="https://discord.gg/CfnpERCY7r" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-400 hover:text-green-300 transition-colors font-medium"
                  >
                    Join our Support Server
                  </a>
                  <p className="text-gray-400 text-sm mt-1">We typically respond within 24 hours</p>
                </div>
              </div>
            </motion.section>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
